import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import { useDispatch } from "react-redux";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import { httpGet, httpPost } from "../../../../../../Utils/apis/apis";
import { setToast } from "../../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";

const useStyles = makeStyles((theme) => ({
  searchStartIcon: {
    color: "var(--color-text-secondary)",
    marginRight: theme.spacing(0.5),
  },
  option: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "4px 0",
  },
  optionTitle: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--color-text-primary)",
  },
  optionSub: {
    fontSize: "0.6875rem",
    color: "var(--color-text-secondary)",
  },
  createOption: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "var(--color-primary-text)",
    fontWeight: 600,
    fontSize: "0.8125rem",
  },
  confirmName: {
    fontWeight: 700,
  },
  input: {
    "& .MuiInput-underline:before": {
      borderBottomColor: "var(--color-text-secondary)",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottomColor: "var(--color-primary-main)",
    },
    "& .MuiInputLabel-root": {
      color: "var(--color-text-secondary)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "var(--color-primary-text)",
    },
    "& .MuiInputBase-input": {
      color: "var(--color-text-primary)",
    },
  },
}));

function EgSearchField({
  id,
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  fetchUrl,
  createUrl,
  createPayloadKey,
  displayKey,
  subtitleRender,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const allRef = useRef(null);

  const [inputValue, setInputValue] = useState(value || "");
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingName, setPendingName] = useState("");

  // Keep input in sync when parent resets the form
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const buildOptions = useCallback(
    (groups, searchTerm) => {
      const trimmed = searchTerm.trim();
      const lower = trimmed.toLowerCase();
      const matched = lower
        ? groups.filter((g) =>
            String(g[displayKey] || "")
              .toLowerCase()
              .includes(lower)
          )
        : groups;

      const hasExactMatch = groups.some(
        (g) =>
          String(g[displayKey] || "")
            .trim()
            .toLowerCase() === lower
      );
      if (trimmed && !hasExactMatch) {
        return [{ isCreate: true, inputValue: trimmed }, ...matched];
      }
      return matched;
    },
    [displayKey]
  );

  const ensureLoaded = useCallback(async () => {
    if (allRef.current) return;
    setIsSearching(true);
    try {
      const data = await httpGet(fetchUrl);
      allRef.current = Array.isArray(data) ? data : [];
    } catch (_) {
      allRef.current = [];
    } finally {
      setIsSearching(false);
    }
  }, [fetchUrl]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    await ensureLoaded();
    setDisplayedOptions(buildOptions(allRef.current || [], ""));
  }, [ensureLoaded, buildOptions]);

  const handleInputChange = useCallback(
    async (_, newInput, reason) => {
      if (reason === "clear") {
        setInputValue("");
        onChange("");
        await ensureLoaded();
        setDisplayedOptions(buildOptions(allRef.current || [], ""));
        setOpen(true);
        return;
      }

      if (reason !== "input") return;

      setInputValue(newInput);
      setOpen(true);
      await ensureLoaded();
      setDisplayedOptions(buildOptions(allRef.current || [], newInput));
    },
    [ensureLoaded, buildOptions, onChange]
  );

  const handleClose = useCallback(
    (_, reason) => {
      if (
        reason === "blur" ||
        reason === "escape" ||
        reason === "toggleInput"
      ) {
        setInputValue(value || "");
        setDisplayedOptions([]);
      }
      setOpen(false);
    },
    [value]
  );

  const handleChange = useCallback(
    (_, newValue) => {
      if (!newValue) {
        onChange("");
        setInputValue("");
        return;
      }
      if (newValue.isCreate) {
        setPendingName(newValue.inputValue);
        setInputValue("");
        setOpen(false);
        setDisplayedOptions([]);
        setConfirmOpen(true);
        return;
      }
      const name = newValue[displayKey];
      onChange(name);
      setInputValue(name);
      setOpen(false);
    },
    [onChange, displayKey]
  );

  const confirmCreate = useCallback(async () => {
    const name = pendingName.trim();
    if (!name) return;
    setIsCreating(true);
    setConfirmOpen(false);
    try {
      const result = await httpPost(createUrl, { [createPayloadKey]: name });
      const saved = result?.[displayKey] || name;

      // Re-fetch to refresh the cache
      try {
        const freshData = await httpGet(fetchUrl);
        allRef.current = Array.isArray(freshData) ? freshData : [];
      } catch (_) {
        // keep stale cache
      }

      onChange(saved);
      setInputValue(saved);
      setPendingName("");

      dispatch(
        setToast({
          type: toastSeverity.SUCCESS,
          message: `Exposure Group "${saved}" created successfully.`,
          open: true,
        })
      );
    } catch (_) {
      dispatch(
        setToast({
          type: toastSeverity.ERROR,
          message: "Failed to create Exposure Group. Please try again.",
          open: true,
        })
      );
    } finally {
      setIsCreating(false);
    }
  }, [
    pendingName,
    createUrl,
    createPayloadKey,
    displayKey,
    fetchUrl,
    onChange,
    dispatch,
  ]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const groups = allRef.current || [];
    return (
      groups.find((g) => g[displayKey] === value) || { [displayKey]: value }
    );
  }, [value, displayKey]);

  return (
    <>
      <Autocomplete
        id={id}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        options={displayedOptions}
        loading={isSearching}
        size="small"
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        filterOptions={(opts) => opts}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (option.isCreate) return option.inputValue || "";
          return String(option[displayKey] || "");
        }}
        getOptionSelected={(option, val) => {
          if (option?.isCreate) return false;
          return option[displayKey] === val?.[displayKey];
        }}
        noOptionsText={isSearching ? "Loading…" : "No groups found"}
        renderOption={(option) => {
          if (option.isCreate) {
            return (
              <div className={classes.createOption}>
                <AddIcon style={{ fontSize: "1rem" }} />
                <span>
                  {option.inputValue
                    ? `Add "${option.inputValue}"`
                    : "Create new exposure group"}
                </span>
              </div>
            );
          }
          return (
            <div className={classes.option}>
              <span className={classes.optionTitle}>{option[displayKey]}</span>
              {subtitleRender ? (
                <span className={classes.optionSub}>
                  {subtitleRender(option)}
                </span>
              ) : (
                <span className={classes.optionSub}>
                  {option.loanCount ?? 0} loans · {option.borrowerCount ?? 0}{" "}
                  borrowers
                </span>
              )}
            </div>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            margin="dense"
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            className={classes.input}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      className={classes.searchStartIcon}
                    />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {(isSearching || isCreating) && (
                    <CircularProgress
                      size={18}
                      style={{
                        color: "var(--color-primary-text)",
                        marginRight: 4,
                      }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <A_SimpleDialog
        open={confirmOpen}
        title="Create Exposure Group"
        dialogContent={
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Typography style={{ fontSize: "0.875rem", lineHeight: 1.6 }}>
              Enter a name for the new exposure group:
            </Typography>
            <TextField
              autoFocus
              fullWidth
              variant="standard"
              placeholder="Exposure group name"
              value={pendingName}
              onChange={(e) => setPendingName(e.target.value)}
              disabled={isCreating}
              className={classes.input}
            />
          </div>
        }
        dialogActions={
          <>
            <A_Button
              label="Cancel"
              premiumVariant="secondary"
              size="small"
              disabled={isCreating}
              onClick={() => {
                setConfirmOpen(false);
                setPendingName("");
              }}
            />
            <A_Button
              label={isCreating ? "Creating…" : "Create"}
              premiumVariant="primary"
              size="small"
              disabled={isCreating || !pendingName.trim()}
              onClick={confirmCreate}
            />
          </>
        }
        onClose={() => {
          if (!isCreating) {
            setConfirmOpen(false);
            setPendingName("");
          }
        }}
        disableBackdropClick={isCreating}
        disableEscapeKeyDown={isCreating}
        maxWidth="xs"
        fullWidth
      />
    </>
  );
}

EgSearchField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  fetchUrl: PropTypes.string.isRequired,
  createUrl: PropTypes.string.isRequired,
  createPayloadKey: PropTypes.string,
  displayKey: PropTypes.string,
  subtitleRender: PropTypes.func,
};

EgSearchField.defaultProps = {
  required: false,
  error: false,
  helperText: "",
  createPayloadKey: "name",
  displayKey: "name",
  subtitleRender: null,
};

export default EgSearchField;
